import type {
    PromozavrQueryParams,
    PromozavrQueueSelection,
    SelectMultiplePromosModelInput,
    SelectMultiplePromosModelServerInput,
    SelectPromoModelDesktopPersistentEntry,
    SelectPromoModelDesktopRegularEntry,
    SelectPromoModelEntry,
    SelectPromoModelInput,
    SelectPromoModelPersistentEntry,
    SelectPromoModelRegularEntry,
    SelectPromoModelResponse,
    SelectPromoModelServerInput,
    SelectPromoSuppliersData,
} from './types/promozavr';
import {
    FETCH_YOURSELF,
    PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID,
    PROMOZAVR_PERSISTENT_PROJECT,
    PROMOZAVR_PERSISTENT_SKIP_CHECKERS,
    PROMOZAVR_PERSISTENT_TOUCH_PROJECT,
    PROMOZAVR_PROJECT,
    PROMOZAVR_STAGE,
    PROMOZAVR_TEST_APP,
    PROMOZAVR_TOUCH_PROJECT,
} from './types/promozavr';

type BuildSelectPromoModelInputArgs = PromozavrQueryParams & {
    lang?: string;
    tld?: string;
    testIds?: string[];
};

type BuildSelectMultiplePromosModelInputArgs = BuildSelectPromoModelInputArgs & {
    isTouch?: boolean;
};

type BuildSsrSelectPromoModelInputArgs = BuildSelectPromoModelInputArgs & {
    fastFail?: boolean;
};

type BuildSsrSelectMultiplePromosModelInputArgs = BuildSelectMultiplePromosModelInputArgs & {
    fastFail?: boolean;
};

type ShouldInitPromozavrArgs = {
    isPaasEnabled: boolean;
    pathname: string;
    isMessenger: boolean;
    isMeetingRoom: boolean;
};

const PROMOZAVR_ALLOWED_PATHS = new Set([
    '/',
    '/connect-to-meeting-by-id',
    '/planning',
    '/broadcast-planning',
    '/create-broadcast',
]);

function splitPromozavrQueryParam(rawParams: string) {
    const params: string[] = [];
    let lastSeparatorIndex = 0;
    let squareBracketDepth = 0;

    for (let index = 0; index < rawParams.length; index += 1) {
        const symbol = rawParams[index];

        if (symbol === '[') {
            squareBracketDepth += 1;
            continue;
        }

        if (symbol === ']') {
            squareBracketDepth = Math.max(squareBracketDepth - 1, 0);
            continue;
        }

        if (symbol === ',' && squareBracketDepth === 0) {
            const param = rawParams.slice(lastSeparatorIndex, index);

            if (param) {
                params.push(param);
            }

            lastSeparatorIndex = index + 1;
        }
    }

    const lastParam = rawParams.slice(lastSeparatorIndex);

    if (lastParam) {
        params.push(lastParam);
    }

    return params;
}

function parsePromozavrRawParams(rawParams: string): PromozavrQueryParams {
    const params: PromozavrQueryParams = {};
    const debugParams: string[] = [];

    splitPromozavrQueryParam(rawParams).forEach((param) => {
        const separatorIndex = param.indexOf(':');

        if (separatorIndex <= 0) {
            debugParams.push(param);

            return;
        }

        const key = param.slice(0, separatorIndex);
        const value = param.slice(separatorIndex + 1);

        if (key === 'app') {
            if (value === PROMOZAVR_TEST_APP) {
                params.app = value;

                return;
            }

            return;
        }

        if (key === 'debug') {
            if (value) {
                params.debug = value;

                return;
            }
        }

        debugParams.push(param);
    });

    if (debugParams.length > 0) {
        params.debugParams = debugParams.join(',');
    }

    return params;
}

function normalizePathname(pathname: string) {
    if (!pathname || pathname === '/') {
        return '/';
    }

    return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function getPromozavrTestIdsFromExperimentBoxes(boxes: string[] = []) {
    return boxes
        .map((box) => {
            const separatorIndex = box.indexOf(',');

            return separatorIndex >= 0 ? box.slice(0, separatorIndex) : box;
        })
        .filter(Boolean);
}

export function createPromozavrSuppliersData(): SelectPromoSuppliersData {
    return {
        bunkerConfigSupplierData: FETCH_YOURSELF,
        billingDataSupplierData: FETCH_YOURSELF,
        billingSubscriptionsDataSupplierData: FETCH_YOURSELF,
        blackboxUserInfoSupplierData: FETCH_YOURSELF,
        datasyncPromoDataSupplierData: FETCH_YOURSELF,
        datasyncUserFeaturesDataSupplierData: FETCH_YOURSELF,
        saasKvSegmentsSupplierData: FETCH_YOURSELF,
    };
}

function buildSelectPromoModelEntry(
    args: BuildSelectPromoModelInputArgs & {
        usePersistentQueues: false;
        isTouch?: false;
    }
): SelectPromoModelDesktopRegularEntry;
function buildSelectPromoModelEntry(
    args: BuildSelectPromoModelInputArgs & {
        usePersistentQueues: true;
        isTouch?: false;
    }
): SelectPromoModelDesktopPersistentEntry;
function buildSelectPromoModelEntry(
    args: BuildSelectPromoModelInputArgs & {
        usePersistentQueues: false;
        isTouch: boolean;
    }
): SelectPromoModelRegularEntry;
function buildSelectPromoModelEntry(
    args: BuildSelectPromoModelInputArgs & {
        usePersistentQueues: true;
        isTouch?: boolean;
    }
): SelectPromoModelPersistentEntry;
function buildSelectPromoModelEntry({
    lang,
    tld,
    testIds = [],
    debugParams,
    usePersistentQueues,
    isTouch = false,
}: BuildSelectPromoModelInputArgs & {
    usePersistentQueues: boolean;
    isTouch?: boolean;
}): SelectPromoModelEntry {
    const entry: SelectPromoModelEntry = {
        project: usePersistentQueues
            ? isTouch
                ? PROMOZAVR_PERSISTENT_TOUCH_PROJECT
                : PROMOZAVR_PERSISTENT_PROJECT
            : isTouch
              ? PROMOZAVR_TOUCH_PROJECT
              : PROMOZAVR_PROJECT,
        stage: PROMOZAVR_STAGE,
        lang,
        tld,
        testIds,
        skipPromos: [],
        selectAllPromos: false,
        usePersistentQueues,
    } as SelectPromoModelEntry;

    if (isTouch) {
        entry.checkerCollectionId = PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID;
    }

    if (entry.usePersistentQueues) {
        entry.skipCheckers = [...PROMOZAVR_PERSISTENT_SKIP_CHECKERS];
    }

    if (debugParams) {
        entry.debugParams = debugParams;
    }

    return entry;
}

export function buildSelectPromoModelInput({
    lang,
    tld,
    testIds = [],
    app,
    debug,
    debugParams,
}: BuildSelectPromoModelInputArgs): SelectPromoModelInput {
    const request: SelectPromoModelInput = {
        ...buildSelectPromoModelEntry({
            lang,
            tld,
            testIds,
            debugParams,
            usePersistentQueues: false,
        }),
        suppliersData: createPromozavrSuppliersData(),
    };

    if (app) {
        request.app = app;
    }

    if (debug) {
        request.debug = debug;
    }

    return request;
}

export function buildSsrSelectPromoModelInput({
    fastFail,
    ...args
}: BuildSsrSelectPromoModelInputArgs): SelectPromoModelServerInput {
    const request: SelectPromoModelServerInput = buildSelectPromoModelInput(args);

    if (fastFail) {
        request.fastFail = true;
    }

    return request;
}

export function buildSelectMultiplePromosModelInput({
    lang,
    tld,
    testIds = [],
    app,
    debug,
    debugParams,
    isTouch = false,
}: BuildSelectMultiplePromosModelInputArgs): SelectMultiplePromosModelInput {
    const request: SelectMultiplePromosModelInput = {
        entries: {
            regular: buildSelectPromoModelEntry({
                lang,
                tld,
                testIds,
                debugParams,
                usePersistentQueues: false,
                isTouch,
            }),
            persistent: buildSelectPromoModelEntry({
                lang,
                tld,
                testIds,
                debugParams,
                usePersistentQueues: true,
                isTouch,
            }),
        },
        suppliersData: createPromozavrSuppliersData(),
    };

    if (app) {
        request.app = app;
    }

    if (debug) {
        request.debug = debug;
    }

    return request;
}

export function buildSsrSelectMultiplePromosModelInput({
    fastFail,
    ...args
}: BuildSsrSelectMultiplePromosModelInputArgs): SelectMultiplePromosModelServerInput {
    const request: SelectMultiplePromosModelServerInput = buildSelectMultiplePromosModelInput(args);

    if (fastFail) {
        request.fastFail = true;
    }

    return request;
}

export function getPromozavrQueueSelection(response?: SelectPromoModelResponse): PromozavrQueueSelection {
    return {
        selectedPromo: response?.selectedPromos?.[0],
        translations: response?.translations,
        images: response?.images,
        isSelected: Boolean(response?.isSelected),
    };
}

export function parsePromozavrQueryParams(search: string): PromozavrQueryParams {
    const rawParams = new URLSearchParams(search).get('promozavr');

    if (!rawParams) {
        return {};
    }

    return parsePromozavrRawParams(rawParams);
}

export function getPromozavrDebugParams(search: string, isYandexNet = false) {
    if (!isYandexNet) {
        return undefined;
    }

    return parsePromozavrQueryParams(search).debugParams;
}

export function isPromozavrRouteAllowed(pathname: string) {
    return PROMOZAVR_ALLOWED_PATHS.has(normalizePathname(pathname));
}

export function getPromozavrRequestKey(pathname: string, search: string) {
    return `${normalizePathname(pathname)}${search}`;
}

export function shouldInitPromozavr({ isPaasEnabled, pathname, isMessenger, isMeetingRoom }: ShouldInitPromozavrArgs) {
    return isPaasEnabled && isPromozavrRouteAllowed(pathname) && !isMessenger && !isMeetingRoom;
}
